/**
 * Shared column-sort helpers for hand-rolled MUI tables.
 *
 * The pattern (established by the Meetings list, generalized here after the
 * 2026-08-14 table/grid audit found user-controlled sorting on only 3 of 45
 * list surfaces): the view's route ViewStore owns `sortKey`/`sortDir` and a
 * computed sorted list over its FILTERED rows; the body renders
 * `SortableHeader` cells wired to `setSort`. These helpers keep the semantics
 * identical across views:
 *
 *   - first click sorts a column in its "natural" direction — dates and
 *     counts newest/biggest-first, text A→Z; a re-click reverses it
 *   - null/undefined values sort LAST in either direction, so "no start
 *     time" never floats to the top of a desc sort
 *   - strings compare case-insensitively (localeCompare, sensitivity base)
 */

import type { SxProps, Theme } from "@mui/material";

export type SortDir = "asc" | "desc";

/**
 * Merge a component's base style with a caller's `sx`, using MUI's ARRAY form.
 *
 * `{ ...base, ...sx }` is the obvious spelling and it is wrong: `SxProps` is a
 * union, and only its plain-object member survives a spread. An array of styles
 * spreads to `{ 0: …, 1: … }` and a `(theme) => …` callback spreads to nothing
 * at all — in both cases silently, with no type error and no runtime warning,
 * so the caller's styling simply never applies. The array form is what MUI
 * documents for exactly this, and it accepts every member of the union.
 */
export function mergeSx(
  base: SxProps<Theme>,
  sx?: SxProps<Theme>
): SxProps<Theme> {
  return [base, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>;
}

export type SortValue = string | number | null;

/**
 * Header-click state transition: same key toggles direction, a new key
 * starts in its natural direction (`descFirst` names the newest/biggest-first
 * columns).
 */
export function nextSort<K extends string>(
  currentKey: K,
  currentDir: SortDir,
  clicked: K,
  descFirst: ReadonlySet<K>
): { key: K; dir: SortDir } {
  if (currentKey === clicked) {
    return { key: clicked, dir: currentDir === "asc" ? "desc" : "asc" };
  }
  return { key: clicked, dir: descFirst.has(clicked) ? "desc" : "asc" };
}

/** Compare two cell values under a direction; nulls last in either direction. */
export function compareSortValues(a: SortValue, b: SortValue, dir: SortDir): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  const d = dir === "asc" ? 1 : -1;
  if (typeof a === "string" && typeof b === "string") {
    return a.localeCompare(b, undefined, { sensitivity: "base" }) * d;
  }
  return (Number(a) - Number(b)) * d;
}

/**
 * Sort rows by a per-row value extractor. Returns a new array (never mutates
 * the input — stores hand this their filtered computed).
 */
export function sortRows<T>(
  rows: readonly T[],
  valueOf: (row: T) => SortValue,
  dir: SortDir
): T[] {
  return rows.slice().sort((a, b) => compareSortValues(valueOf(a), valueOf(b), dir));
}

/** Normalize an ISO date string to a sortable number, null-safe. */
export function dateValue(value: string | null | undefined): number | null {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isFinite(t) ? t : null;
}

/** Normalize a string cell, mapping empty/whitespace to null (sorts last). */
export function textValue(value: string | null | undefined): string | null {
  const t = value?.trim();
  return t ? t : null;
}
